import React, { useCallback, useEffect, useState } from 'react';
import {
  barrierAPI,
  camerasAPI,
  type BarrierOpenOn,
  type BarrierSettings,
} from '../../utils/api';
import { parseDirectionVector } from '../../utils/directionVector';
import '../../pages/Area/Area.css';
import './BarrierModal.css';

interface BarrierModalProps {
  camera: {
    camera_id: string | null;
    name?: string;
  };
  onClose: () => void;
  /** Вызывается после сохранения/удаления — чтобы родитель обновил своё состояние. */
  onUpdated?: (barrier: BarrierSettings | null) => void;
}

const DEFAULT_COOLDOWN = 15;

/** Тип шлагбаума (open_any_plate). */
const BARRIER_TYPE_OPTIONS: { value: boolean; label: string; hint: string }[] = [
  { value: false, label: 'Въездной', hint: 'открывать номерам из базы участка и гостям' },
  { value: true, label: 'Выездной', hint: 'выпускать любую распознанную машину' },
];

const OPEN_ON_OPTIONS: { value: BarrierOpenOn; label: string; needsVector: boolean }[] = [
  { value: 'entry', label: 'Только на въезд', needsVector: true },
  { value: 'exit', label: 'Только на выезд', needsVector: true },
  { value: 'any', label: 'На любой проезд', needsVector: false },
];

/** Формат для показа: 7XXXXXXXXXX → +7 XXX XXX-XX-XX. */
function formatPhoneForDisplay(raw: string): string {
  const digits = (raw || '').replace(/\D+/g, '');
  if (digits.length !== 11) return raw || '';
  const d = digits[0] === '8' ? '7' + digits.slice(1) : digits;
  return `+${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}

/** Принимаем +7…, 8…, с пробелами; null — номер некорректный. */
function normalizePhone(raw: string): string | null {
  let digits = (raw || '').replace(/\D+/g, '');
  if (digits.length === 11 && digits[0] === '8') digits = '7' + digits.slice(1);
  if (digits.length === 10) digits = '7' + digits;
  if (digits.length !== 11 || digits[0] !== '7') return null;
  return digits;
}

/**
 * Настройки автооткрытия шлагбаума. entry/exit доступны только при заданном
 * векторе направления — внешний API иначе отвечает 400.
 */
const BarrierModal: React.FC<BarrierModalProps> = ({ camera, onClose, onUpdated }) => {
  const cameraId = camera.camera_id;

  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [phone, setPhone] = useState('');
  const [openOn, setOpenOn] = useState<BarrierOpenOn>('any');
  /** false — въездной (по умолчанию), true — выездной. */
  const [openAnyPlate, setOpenAnyPlate] = useState(false);
  const [cooldown, setCooldown] = useState(String(DEFAULT_COOLDOWN));
  const [hasVector, setHasVector] = useState(false);
  const [configured, setConfigured] = useState(false);

  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!cameraId) return;
    setLoading(true);
    setError(null);

    const [barrierRes, vectorRes] = await Promise.allSettled([
      barrierAPI.get(cameraId),
      camerasAPI.getDirectionVector(cameraId),
    ]);

    if (vectorRes.status === 'fulfilled' && vectorRes.value.success) {
      setHasVector(parseDirectionVector(vectorRes.value.vector) !== null);
    } else {
      setHasVector(false);
    }

    if (barrierRes.status === 'fulfilled') {
      const barrier = barrierRes.value.barrier;
      if (barrierRes.value.success && barrier) {
        setConfigured(true);
        setEnabled(barrier.enabled);
        setPhone(formatPhoneForDisplay(barrier.phone));
        setOpenOn(barrier.open_on);
        setOpenAnyPlate(!!barrier.open_any_plate);
        setCooldown(String(barrier.call_cooldown_sec ?? DEFAULT_COOLDOWN));
      } else if (!barrierRes.value.success) {
        setError(barrierRes.value.message || 'Не удалось загрузить настройки шлагбаума');
      }
    } else {
      setError(barrierRes.reason?.message || 'Не удалось загрузить настройки шлагбаума');
    }

    setLoading(false);
  }, [cameraId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Вектор пропал (сбросили в другом окне), а стоял entry/exit — возвращаемся на any
  useEffect(() => {
    if (!hasVector && (openOn === 'entry' || openOn === 'exit')) {
      setOpenOn('any');
    }
  }, [hasVector, openOn]);

  const phoneNormalized = normalizePhone(phone);
  const phoneInvalid = phone.trim() !== '' && phoneNormalized === null;
  const cooldownValue = parseInt(cooldown, 10);
  const cooldownInvalid = Number.isNaN(cooldownValue) || cooldownValue < 0;

  const handleSave = async () => {
    if (!cameraId) return;
    setError(null);
    setMessage(null);

    if (!phoneNormalized) {
      setError('Укажите номер SIM-карты модуля. Пример: +7 900 000-00-00');
      return;
    }
    if (cooldownInvalid) {
      setError('Задержка между звонками должна быть числом не меньше 0');
      return;
    }

    try {
      setSaving(true);
      const res = await barrierAPI.save(cameraId, {
        phone: phoneNormalized,
        open_on: openOn,
        call_cooldown_sec: cooldownValue,
        enabled,
        open_any_plate: openAnyPlate,
      });

      if (!res.success) {
        setError(res.message || 'Не удалось сохранить настройки шлагбаума');
        return;
      }

      setConfigured(true);
      setMessage(
        enabled ? 'Настройки сохранены' : 'Автооткрытие выключено, настройки сохранены'
      );
      if (onUpdated) onUpdated(res.barrier ?? null);
    } catch (e: any) {
      setError(e?.message || 'Не удалось сохранить настройки шлагбаума');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenNow = async () => {
    if (!cameraId) return;
    setError(null);
    setMessage(null);
    try {
      setOpening(true);
      const res = await barrierAPI.open(cameraId);
      if (!res.success) {
        setError(res.message || 'Не удалось открыть шлагбаум');
        return;
      }
      setMessage(res.message || 'Команда на открытие отправлена');
    } catch (e: any) {
      setError(e?.message || 'Не удалось открыть шлагбаум');
    } finally {
      setOpening(false);
    }
  };

  const handleRemove = async () => {
    if (!cameraId) return;
    if (!window.confirm('Удалить настройки шлагбаума? Автооткрытие перестанет работать.')) return;

    setError(null);
    setMessage(null);
    try {
      setRemoving(true);
      const res = await barrierAPI.remove(cameraId);
      if (!res.success) {
        setError(res.message || 'Не удалось удалить настройки шлагбаума');
        return;
      }
      setConfigured(false);
      setPhone('');
      setOpenOn('any');
      setOpenAnyPlate(false);
      setCooldown(String(DEFAULT_COOLDOWN));
      setEnabled(true);
      setMessage('Настройки шлагбаума удалены');
      if (onUpdated) onUpdated(null);
    } catch (e: any) {
      setError(e?.message || 'Не удалось удалить настройки шлагбаума');
    } finally {
      setRemoving(false);
    }
  };

  const busy = saving || opening || removing;

  return (
    <div className="area-modal" role="dialog" aria-modal="true">
      <div className="area-modal__backdrop" onClick={onClose} />
      <div className="area-modal__dialog area-modal__dialog--small barrier__dialog">
        <header className="area-modal__header">
          <h2 className="area-modal__title">Шлагбаум</h2>
          <label className="barrier__toggle">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={loading || busy}
            />
            <span className="barrier__toggle-track" aria-hidden />
            <span className="barrier__toggle-label">{enabled ? 'вкл' : 'выкл'}</span>
          </label>
          <button className="area-modal__close" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>

        <div className="barrier__body">
          {loading ? (
            <div className="ui-loading">Загрузка настроек…</div>
          ) : (
            <>
              <div className="barrier__field">
                <label className="barrier__label" htmlFor="barrier-phone">
                  Номер SIM-карты модуля
                </label>
                <input
                  id="barrier-phone"
                  type="tel"
                  className={'area-modal__input' + (phoneInvalid ? ' barrier__input--error' : '')}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setError(null);
                  }}
                  placeholder="+7 999 123-45-67"
                  disabled={busy}
                  aria-invalid={phoneInvalid}
                />
                <span className="barrier__hint">можно вводить через +7 или 8</span>
              </div>

              <fieldset className="barrier__field barrier__fieldset" disabled={busy}>
                <legend className="barrier__label">Тип шлагбаума</legend>
                {BARRIER_TYPE_OPTIONS.map((option) => (
                  <label key={String(option.value)} className="barrier__radio">
                    <input
                      type="radio"
                      name="barrier-type"
                      checked={openAnyPlate === option.value}
                      onChange={() => setOpenAnyPlate(option.value)}
                    />
                    <span>{option.label}</span>
                    <span className="barrier__radio-note">— {option.hint}</span>
                  </label>
                ))}
              </fieldset>

              <fieldset className="barrier__field barrier__fieldset" disabled={busy}>
                <legend className="barrier__label">Когда открывать</legend>
                {OPEN_ON_OPTIONS.map((option) => {
                  const blocked = option.needsVector && !hasVector;
                  return (
                    <label
                      key={option.value}
                      className={'barrier__radio' + (blocked ? ' barrier__radio--blocked' : '')}
                    >
                      <input
                        type="radio"
                        name="barrier-open-on"
                        value={option.value}
                        checked={openOn === option.value}
                        onChange={() => setOpenOn(option.value)}
                        disabled={blocked}
                      />
                      <span>{option.label}</span>
                      {blocked && (
                        <span className="barrier__radio-hint">
                          Сначала нарисуйте направление въезда
                        </span>
                      )}
                    </label>
                  );
                })}
              </fieldset>

              <div className="barrier__field">
                <label className="barrier__label" htmlFor="barrier-cooldown">
                  Не звонить чаще, чем раз в (сек)
                </label>
                <input
                  id="barrier-cooldown"
                  type="number"
                  min={0}
                  step={1}
                  className={'area-modal__input' + (cooldownInvalid ? ' barrier__input--error' : '')}
                  value={cooldown}
                  onChange={(e) => setCooldown(e.target.value)}
                  disabled={busy}
                />
              </div>

              {error && <div className="barrier__message barrier__message--error">{error}</div>}
              {message && !error && (
                <div className="barrier__message barrier__message--success">{message}</div>
              )}
            </>
          )}
        </div>

        <footer className="area-modal__footer barrier__footer">
          {configured && (
            <>
              <button
                type="button"
                className="area-modal__submit area-modal__submit--secondary"
                onClick={() => void handleOpenNow()}
                disabled={loading || busy}
              >
                {opening ? 'Открываем…' : 'Открыть сейчас'}
              </button>
              <button
                type="button"
                className="area-modal__submit area-modal__submit--danger"
                onClick={() => void handleRemove()}
                disabled={loading || busy}
              >
                {removing ? 'Удаляем…' : 'Удалить'}
              </button>
            </>
          )}
          <button
            type="button"
            className="area-modal__submit"
            onClick={() => void handleSave()}
            disabled={loading || busy || !phoneNormalized || cooldownInvalid}
          >
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default BarrierModal;
